/**
 * Canonical public origin of the site — the single source of truth.
 *
 * Imported by `nuxt.config.ts` (as `site.url`, which @nuxtjs/robots writes into
 * robots.txt), re-exported by `server/utils/site.ts` for the sitemap, and used
 * by pages that need absolute URLs for `<link rel="canonical">`, Open Graph tags
 * and JSON-LD. Prerendered pages can't read the request host, so this has to be
 * a constant rather than something derived at runtime.
 */
export const SITE_URL = 'https://colorize.design'

/**
 * Where every "API" call-to-action points.
 *
 * The API is sold and documented on Prefetch, not here — the local /api page is
 * marketing copy that predates that, so links go straight to the product. Kept
 * as a constant so the destination moves in one edit.
 *
 * Don't link to this bare — use `apiUrl(placement)` below so the click is
 * attributable once it lands on the other side.
 */
export const API_URL = 'https://prefetch.io'

/**
 * Attribution params carried on every outbound link to Prefetch.
 *
 * `ref` is the plain-language one, readable in a server log without an
 * analytics tool in front of it; the `utm_*` set is what GA4 (and every other
 * analytics product) picks up on its own, with no custom dimensions to
 * configure on the Prefetch side. Both say the same thing on purpose —
 * whichever one Prefetch ends up reading, the answer is the same.
 */
const REFERRAL_PARAMS: Record<string, string> = {
  ref: 'colorize.design',
  utm_source: 'colorize.design',
  utm_medium: 'referral',
  utm_campaign: 'colorize_api',
}

/**
 * Build the Prefetch URL for one specific call-to-action.
 *
 * Two questions this is meant to answer on the Prefetch dashboard:
 *
 *   - *which CTA* did they click — `utm_content`, e.g. `nav`, `footer_logo`,
 *     `api_card`. Use the same slug for the same button everywhere it appears
 *     so the totals stay comparable, and keep it in step with the `trackEvent`
 *     name fired alongside it, so a click can be followed from Clarity/GA on
 *     this side to the session on the other.
 *   - *which page* were they on — `utm_term`. The path, not the full URL:
 *     query strings here carry things like the generator's `?url=` and the
 *     search term, which are none of Prefetch's business. `utm_term` is
 *     nominally a paid-search keyword field, but it is the only spare slot
 *     GA4 reports on without setup, and "the page that sent them" is the
 *     thing worth having there.
 *
 * Every caller is either prerendered or client-rendered from the same route,
 * so the string is stable across SSR and hydration.
 */
export function apiUrl(placement: string, page?: string): string {
  return withReferral(`${API_URL}/`, placement, page)
}

/** The Prefetch MCP server's setup guide — every AI client's config, in one page. */
export const MCP_DOCS_URL = 'https://docs.prefetch.io/mcp/overview'

/** `apiUrl` for the MCP docs: same attribution, so a docs visit traces back too. */
export function mcpDocsUrl(placement: string, page?: string): string {
  return withReferral(MCP_DOCS_URL, placement, page)
}

function withReferral(base: string, placement: string, page?: string): string {
  const params = new URLSearchParams({ ...REFERRAL_PARAMS, utm_content: placement })
  if (page) params.set('utm_term', page)
  return `${base}?${params.toString()}`
}

/**
 * The Chrome Web Store listing for the Colorize extension.
 *
 * Every "add to browser" button on the site points here — Chrome, Brave, Edge,
 * Arc, Opera and Vivaldi all install from this one listing, so the destination
 * is a constant and only the button's label changes (see
 * composables/useBrowserDetect.ts).
 *
 * The two footers link to `/browser-extension` rather than straight here: that
 * page is what carries the install button, and it's the one thing in the footer
 * that has an indexable page of its own to send crawlers to.
 */
export const EXTENSION_URL =
  'https://chromewebstore.google.com/detail/website-color-palette-ext/pmbkklgeabiedckclijbgpebcpgoiikd'
